import type { EssenceType } from './essence';

/**
 * Visions: memories the Veil kept, surfacing to some of the players present
 * when a Crucible's heart shatters, and kept in each player's Compendium
 * (docs/gameplay/game-intro.md, "Visions and the Compendium"). Other people's
 * memories, shown without context.
 */

/** Where a vision's painted plate comes from: a cutscene panel's image, so both show one painting. */
export interface VisionPlate {
    cutscene: string;
    panel: string;
}

/** A vision as authored in `game-content/data/visions/<id>.vision.json`. */
export interface Vision {
    /** Stable slug; matches the file name. */
    id: string;
    title: string;
    /** The essence it carries; its emblem marks the vision's page. */
    essence: EssenceType;
    /** What is seen and felt, paragraph by paragraph. */
    text: string[];
    /** Its painted plate. The API serves it as `plateUrl` once the panel has an image. */
    plate?: VisionPlate;
}

/**
 * A vision a character has received, as `GET .../characters/:characterId/visions`
 * lists it (in reading order: each chapter's in turn, then the loose pages).
 */
export interface ReceivedVision {
    visionId: string;
    title: string;
    essence: EssenceType;
    text: string[];
    /** URL path of its painted plate, once its art exists. */
    plateUrl?: string;
    receivedAt: string;
    /** The Crucible whose heart it surfaced from, when that run is still on record. */
    crucibleName?: string;
    /** The Compendium chapter the player keeps it in: `LOOSE_PAGES_CHAPTER_ID` until they gather it into one. */
    chapterId: string;
    /** 0-based place within its chapter. */
    position: number;
}

/**
 * A chapter of the Compendium, as `GET .../compendium/chapters` lists them:
 * the player's own, in their order, then the loose pages. Its visions name it
 * as their `chapterId`.
 */
export interface CompendiumChapter {
    id: string;
    name: string;
    /**
     * The loose pages: the chapter every vision starts in, always last. It
     * can't be renamed, moved or removed, and a page leaves it only by
     * joining another chapter.
     */
    isLoose: boolean;
}

/** The id of the loose pages, the special chapter every Compendium has. */
export const LOOSE_PAGES_CHAPTER_ID = 'loose';

/** The name the loose pages go by. */
export const LOOSE_PAGES_NAME = 'Loose Pages';

/** Body of `POST .../compendium/chapters`: a new chapter, added last. */
export interface CreateCompendiumChapterRequest {
    name: string;
}

/** Body of `PATCH .../compendium/chapters/:chapterId`: a new name, a new place among the chapters, or both. */
export interface UpdateCompendiumChapterRequest {
    name?: string;
    /** 0-based index among the chapters. */
    position?: number;
}

/**
 * Body of `PUT .../compendium/chapters/:chapterId/visions`: the chapter's
 * visions, exactly and in order. A listed vision kept elsewhere moves here;
 * one the chapter held but the list leaves out goes to the end of the loose
 * pages (for the loose pages themselves, it stays, after the listed ones).
 */
export interface SetCompendiumChapterVisionsRequest {
    visionIds: string[];
}

/**
 * Body of `PUT .../compendium/chapters/:chapterId/visions/:visionId`: where
 * the vision goes in the chapter (it is added, or moved within it).
 */
export interface PlaceCompendiumVisionRequest {
    /** 0-based index within the chapter, clamped; the end when omitted. */
    position?: number;
}

/** The longest chapter name a player can give. */
export const COMPENDIUM_CHAPTER_NAME_MAX_LENGTH = 60;

/** The most chapters a character's compendium holds, besides the loose pages. */
export const COMPENDIUM_CHAPTER_MAX_COUNT = 50;
